import { ColumnDef } from "@tanstack/react-table";
import { OrganizationStatus } from "@/features-root-user/service/organization";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Organization } from "../types";
import { DataTableRowActions } from "./data-table-row-actions";
import { OrganizationStatusForm } from "./organization-status-form";
import { ResponsiblePersonForm } from "./responsible-person-form";
import { UserCountCell } from "./user-count-cell";
import { OnboardingCountCell } from "./onboarding-count-cell";

function dateSort(rowA: any, rowB: any, columnId: string) {
  const dateA = new Date(rowA.getValue(columnId));
  const dateB = new Date(rowB.getValue(columnId));
  return dateA.getTime() - dateB.getTime();
}

// Extend the Organization type to include the onboarding count
type EnhancedOrganization = Organization;

export const columns: ColumnDef<EnhancedOrganization>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    meta: {
      className: cn(
        "sticky md:table-cell left-0 z-10 rounded-tl",
        "bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted",
      ),
    },
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    meta: {
      className: cn(
        "sticky left-6 md:table-cell",
        "bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted",
      ),
    },
    enableHiding: false,
  },
  {
    accessorKey: "slug",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Slug" />
    ),
  },
  {
    accessorKey: "domain",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Domain" />
    ),
  },
  {
    accessorKey: "responsiblePerson",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Responsible Person" />
    ),
    cell: ({ row }) => (
      <ResponsiblePersonForm
        organizationId={row.original.id}
        person={row.original.responsiblePerson}
        popoverIdentifier={`responsible-person-${row.original.name}`}
        jiraTicket={row.original.jiraTicket}
      />
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <OrganizationStatusForm
        organizationId={row.original.id}
        status={row.getValue<OrganizationStatus>("status")}
        popoverIdentifier={`status-${row.original.name}`}
      />
    ),
    filterFn: (row, id, value: ReadonlyArray<string>) => {
      return value.includes(row.getValue(id));
    },
    enableHiding: true,
  },
  {
    id: "userCounts",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Users / MAU / ON" />
    ),
    cell: ({ row }) => (
      <div className="flex space-x-1">
        <UserCountCell organizationId={row.original.id} />
        <span>/</span>
        <OnboardingCountCell organizationId={row.original.id} />
      </div>
    ),
    accessorFn: (row) => row.id,
    enableSorting: true,
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const dateVal = row.getValue<Date>("createdAt");
      return <span>{new Date(dateVal).toLocaleDateString()}</span>;
    },
    enableSorting: true,
    sortingFn: dateSort,
  },
  {
    accessorKey: "updatedAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: ({ row }) => {
      const dateVal = row.getValue<Date>("updatedAt");
      return <span>{new Date(dateVal).toLocaleDateString()}</span>;
    },
    enableSorting: true,
    sortingFn: dateSort,
  },
  {
    id: "actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
